import { useState } from "react";
import api from "@/lib/api";
import { PublicNav, Footer } from "@/components/layout/PublicLayout";
import { SectionHead } from "./Landing";
import { useContent } from "@/context/ContentContext";
import { toast } from "sonner";
import { Mail, MapPin, MessageSquare, Loader2, CheckCircle2 } from "lucide-react";

const LEGAL = {
  terms: {
    titleKey: ["legal.terms.title", "Conditions d'utilisation"],
    body: [
      ["legal.terms.s1.h", "1. Acceptation", "legal.terms.s1.b", "En utilisant The World Promotions, vous acceptez les présentes conditions d'utilisation."],
      ["legal.terms.s2.h", "2. Utilisation du service", "legal.terms.s2.b", "Vous vous engagez à utiliser les services conformément aux règles des plateformes tierces concernées et aux lois applicables."],
      ["legal.terms.s3.h", "3. Portefeuille et paiements", "legal.terms.s3.b", "Le portefeuille interne est libellé en USD. Tous les mouvements sont enregistrés dans un registre de transactions."],
      ["legal.terms.s4.h", "4. Limitation de responsabilité", "legal.terms.s4.b", "Les résultats des services dépendent des fournisseurs. Aucune garantie non fondée n'est offerte."],
      ["legal.terms.s5.h", "5. Modification", "legal.terms.s5.b", "Ce texte est un espace réservé et devra être révisé selon la juridiction réelle de l'entreprise."],
    ],
  },
  privacy: {
    titleKey: ["legal.privacy.title", "Politique de confidentialité"],
    body: [
      ["legal.privacy.s1.h", "1. Données collectées", "legal.privacy.s1.b", "Nous collectons les informations nécessaires à la fourniture du service : email, nom, historique de commandes et de transactions."],
      ["legal.privacy.s2.h", "2. Utilisation", "legal.privacy.s2.b", "Vos données servent à gérer votre compte, vos commandes et votre support. Elles ne sont jamais vendues."],
      ["legal.privacy.s3.h", "3. Sécurité", "legal.privacy.s3.b", "Les mots de passe sont hachés (bcrypt). Les clés API des fournisseurs ne sont jamais exposées côté client."],
      ["legal.privacy.s4.h", "4. Cookies", "legal.privacy.s4.b", "Un cookie de session sécurisé est utilisé pour l'authentification."],
      ["legal.privacy.s5.h", "5. Contact", "legal.privacy.s5.b", "Pour toute demande relative à vos données, contactez notre support."],
    ],
  },
  refund: {
    titleKey: ["legal.refund.title", "Politique de remboursement"],
    body: [
      ["legal.refund.s1.h", "1. Échec fournisseur", "legal.refund.s1.b", "En cas d'échec de livraison, la commande est automatiquement remboursée sur votre portefeuille."],
      ["legal.refund.s2.h", "2. Livraison partielle", "legal.refund.s2.b", "Si une commande est partiellement livrée, le solde non livré est remboursé au prorata."],
      ["legal.refund.s3.h", "3. Annulation", "legal.refund.s3.b", "Les remboursements pour annulation dépendent de l'état d'avancement et de la politique du fournisseur."],
      ["legal.refund.s4.h", "4. Registre", "legal.refund.s4.b", "Chaque remboursement génère une transaction dans le registre du portefeuille. Aucun solde n'est modifié silencieusement."],
    ],
  },
};

export function LegalPage({ type }) {
  const { t } = useContent();
  const p = LEGAL[type];
  return (
    <div>
      <PublicNav />
      <div className="mx-auto max-w-3xl px-5 py-16">
        <SectionHead eyebrow={t("legal.eyebrow", "Légal")} title={t(p.titleKey[0], p.titleKey[1])} subtitle={t("legal.updated", "Dernière mise à jour appliquée à l'ensemble de la plateforme.")} />
        <div className="mt-8 space-y-6">
          {p.body.map(([hk, hf, bk, bf]) => (
            <div key={hk}>
              <h3 className="font-display font-semibold">{t(hk, hf)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t(bk, bf)}</p>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function Contact() {
  const { t } = useContent();
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error(t("contact.err", "Veuillez remplir tous les champs (message d'au moins 5 caractères)."));
      return;
    }
    setBusy(true);
    try {
      await api.post("/support/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("contact.err_send", "Échec de l'envoi. Réessayez."));
    } finally { setBusy(false); }
  };
  return (
    <div>
      <PublicNav />
      <div className="mx-auto grid max-w-5xl gap-10 px-5 py-16 md:grid-cols-2">
        <div>
          <SectionHead eyebrow={t("contact.eyebrow", "Contact")} title={t("contact.title", "Nous contacter")} subtitle={t("contact.subtitle", "Une question ? Notre équipe vous répond.")} />
          <div className="mt-8 space-y-4 text-sm">
            <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-accent" /> support@theworldpromotions.com</div>
            <div className="flex items-center gap-3"><MessageSquare className="h-5 w-5 text-accent" /> {t("contact.chat_line", "Support en direct via votre tableau de bord")}</div>
            <div className="flex items-center gap-3"><MapPin className="h-5 w-5 text-accent" /> {t("contact.location_line", "Service international · en ligne")}</div>
          </div>
        </div>
        <form data-testid="contact-form" onSubmit={submit} className="rounded-xl border border-border bg-card p-6">
          {sent ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="contact-sent">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              <div className="text-sm text-muted-foreground">{t("contact.sent_msg", "Votre message a bien été envoyé. Nous vous répondrons par email.")}</div>
            </div>
          ) : (
            <div className="space-y-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("contact.name_ph", "Votre nom")} data-testid="contact-name" className="w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("contact.email_ph", "Votre email")} data-testid="contact-email" className="w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
              <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("contact.message_ph", "Votre message")} rows={5} data-testid="contact-message" className="w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
              <button type="submit" disabled={busy} data-testid="contact-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("contact.send_btn", "Envoyer")}</button>
            </div>
          )}
        </form>
      </div>
      <Footer />
    </div>
  );
}
